import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Box } from '@mui/material'

import { Button, ButtonText, CardContent, CardDashboard, Loader, Spacer, Menu } from '@percent/lemonade'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { useMutation, usePermissions, useQuery } from '@percent/admin-dashboard/common/hooks'
import { ErrorView } from '@percent/admin-dashboard/common/components'
import { formatMoney } from '@percent/utility'
import { ReactComponent as DonationsIcon } from '@percent/admin-dashboard/common/assets/images/donations_icon.svg'
import { ReassignDonationModal } from '@percent/admin-dashboard/common/components/reassignDonationModal/ReassignDonationModal'
import { DonationReassignmentProps } from '@percent/admin-dashboard/common/components/reassignDonationModal/ReassignDonationModal.types'
import {
  usePostHogFeatureFlag,
  POSTHOG_FEATURE_FLAGS
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag'
import styles from './Donations.module.scss'
import { AddToReassignmentQueueAllModal } from '@percent/admin-dashboard/app/donations/reassignDonation/addToReassignmentQueueAllModal/AddToReassignmentQueueAllModal'
import { ReassignAllMarkedDonationsModal } from '@percent/admin-dashboard/app/donations/reassignDonation/reassignAllMarkedDonationsModal/ReassignAllMarkedDonationsModal'
import { CancelAllReassignmentsModal } from '@percent/admin-dashboard/common/components/cancelAllReassignmentsModal/CancelAllReassignmentsModal'
import { ReassignmentKind } from '@percent/admin-dashboard/api/actions/donations/donations.types'

export function Donations({ organisationId, organisationName }: { organisationId: string; organisationName: string }) {
  const {
    t,
    i18n: { language: locale }
  } = useTranslation()
  const { donationsService } = useServices()
  const navigate = useNavigate()
  const reassignDonationFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.AD_REASSIGN_DONATION)
  const internalFgaDoraDonationsFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.INTERNAL_FGA_DORA_DONATIONS)
  const { userHasPermissions } = usePermissions()

  const canReassign =
    reassignDonationFeatureFlag &&
    (!internalFgaDoraDonationsFeatureFlag || userHasPermissions('donations:can_reassign'))

  const [isReassignDonationModalOpen, setIsReassignDonationModalOpen] = useState(false)
  const [isReassignMarkedDonationModalOpen, setIsReassignMarkedDonationModalOpen] = useState(false)
  const [isSelectReassignOptionOpen, setIsSelectReassignOptionOpen] = useState(false)
  const [errorDialogState, setErrorDialogState] = useState(false)
  const [successDialogState, setSuccessDialogState] = useState(false)

  const [isAddAllToReassignmentQueueModalOpen, setIsAddAllToReassignmentQueueModalOpen] = useState(false)
  const [isCancelReassignmentsModalOpen, setIsCancelReassignmentsModalOpen] = useState(false)

  const [{ data, isLoading, errorMessage }, { refresh }] = useQuery(donationsService.getTotalDonationsWithAmount, {
    organisationId,
    currencyCode: 'USD'
  })

  const handleGoToDonationsPage = async () => {
    return navigate(`/donations?organisationId=${organisationId}`)
  }

  const handleReassignDonationsModalOpen = () => {
    setIsReassignDonationModalOpen(prevState => !prevState)
  }

  const handleAddAllToReassignmentQueueModalOpen = () => {
    setIsAddAllToReassignmentQueueModalOpen(prevState => !prevState)
  }

  const handleReassignMarkedDonationsModalOpen = () => {
    setIsReassignMarkedDonationModalOpen(prevState => !prevState)
  }

  const handleCancelReassignmentsModalOpen = () => {
    setIsCancelReassignmentsModalOpen(prevState => !prevState)
  }

  const [{ isLoading: isReassignDonationsLoading, error: reassignError }, { apiFunc: reassignAllApiFunc }] =
    useMutation(
      donationsService.reassignAllDonations,
      () => setSuccessDialogState(true),
      () => setErrorDialogState(true)
    )

  const reassignAllDonations = async ({ organisationId: organisationIdTo, reason }: DonationReassignmentProps) => {
    if (reassignDonationFeatureFlag) {
      await reassignAllApiFunc({
        organisationId,
        payload: {
          organisationIdTo,
          reason,
          reassignmentKind: ReassignmentKind.Merge
        }
      })
    }
  }

  if (isLoading) {
    return <Loader />
  }

  const cardActions = (
    <div className={styles.cardActionsContainer}>
      {reassignDonationFeatureFlag && (
        <>
          <Button
            onClick={handleCancelReassignmentsModalOpen}
            variant="secondary"
            data-testid="cancel-reassignments-button"
            disabled={!canReassign}
          >
            {t('typography.cancelReassignments')}
          </Button>
          <Button
            onClick={handleAddAllToReassignmentQueueModalOpen}
            variant="secondary"
            data-testid="add-all-donations-to-reassign-queue-button"
            disabled={!canReassign}
          >
            {t('typography.addAllToReassignmentQueue')}
          </Button>
          <Menu
            onChange={setIsSelectReassignOptionOpen}
            customTriggerComponent={
              <Button
                endIcon={isSelectReassignOptionOpen ? 'chevron-up' : 'chevron-down'}
                variant="secondary"
                endIconSize={5}
                data-testid="reassign-menu-button"
                disabled={!canReassign}
              >
                {t('button.reassign')}
              </Button>
            }
            title={t('button.reassign')}
            sections={[
              {
                items: [
                  {
                    key: 'reassignAll',
                    label: t('typography.reassignToNewOrganisation'),
                    onClick: handleReassignDonationsModalOpen,
                    shouldBeDisabled: !canReassign
                  },
                  {
                    key: 'reassignAllMarked',
                    label: t('typography.reassignMarkedToNewOrganisation'),
                    onClick: handleReassignMarkedDonationsModalOpen,
                    shouldBeDisabled: !canReassign
                  }
                ]
              }
            ]}
          />
        </>
      )}
    </div>
  )

  return (
    <>
      <CardDashboard title={t('typography.donations')} action={reassignDonationFeatureFlag ? cardActions : undefined}>
        {errorMessage ? (
          <ErrorView errorMessage={errorMessage} />
        ) : (
          <>
            <CardContent title={t('typography.totalDonationsCount')} testId="donations-total-count">
              <Box className={styles.countContainer}>
                {data.count}
                <ButtonText onClick={handleGoToDonationsPage}>
                  <Box className={styles.viewDonationsButtonContainer}>
                    <DonationsIcon fill="black" className={styles.icon} />
                    {t('typography.viewDonations')}
                  </Box>
                </ButtonText>
              </Box>
            </CardContent>
            <CardContent title={t('typography.totalDonationsAmount')} testId="donations-total-amount">
              {formatMoney(data.converted, { locale })} ({t('typography.convertedInfo')})
            </CardContent>
          </>
        )}
      </CardDashboard>
      <Spacer size={6} axis="vertical" />
      {reassignDonationFeatureFlag && (
        <>
          <AddToReassignmentQueueAllModal
            open={isAddAllToReassignmentQueueModalOpen}
            refresh={refresh}
            sourceOrganisationName={organisationName}
            selectedOrganisation={{ id: organisationId, name: organisationName }}
            onClose={() => setIsAddAllToReassignmentQueueModalOpen(false)}
          />
          <ReassignAllMarkedDonationsModal
            open={isReassignMarkedDonationModalOpen}
            refresh={refresh}
            sourceOrganisationName={organisationName}
            onClose={() => setIsReassignMarkedDonationModalOpen(false)}
            currentOrganisationId={organisationId}
          />
          <ReassignDonationModal
            open={isReassignDonationModalOpen}
            refresh={refresh}
            sourceOrganisationName={organisationName}
            onClose={() => setIsReassignDonationModalOpen(false)}
            isReassignAll
            serviceProps={{
              apiFunc: reassignAllDonations,
              errorDialogState,
              success: successDialogState,
              error: reassignError,
              setErrorDialogState,
              setSuccessDialogState,
              isLoading: isReassignDonationsLoading
            }}
          />
          <CancelAllReassignmentsModal
            open={isCancelReassignmentsModalOpen}
            refresh={refresh}
            sourceOrganisationName={organisationName}
            organisationId={organisationId}
            onClose={() => setIsCancelReassignmentsModalOpen(false)}
          />
        </>
      )}
    </>
  )
}
